import React, { useState, useCallback, useMemo } from 'react';
import { inject, observer } from 'mobx-react';
import { FormattedMessage, injectIntl, intlShape } from 'react-intl';
import { get, flatten } from 'lodash';
import { useHistory } from 'react-router-dom';
import { Alert } from 'reactstrap';
import PropTypes from 'prop-types';

import { modelOf } from '../../../prop-types';
import PageTitle from '../../common/PageTitle';
import SEOTitle from '../../head/SEOTitle';
import AddressStore from '../../../store/AddressStore';
import UnexpectedError from '../../loader/UnexpectedError';
import AddressForm from '../../address/AddressForm';
import globalTranslations from '../../../i18n/globalTranslations';
import RouteService from '../../../services/RouteService';
import Paths from '../../../types/Paths';
import HelperNavigation from '../../common/HelperNavigation';
import RequestState from '../../../types/RequestState';

export const MyAccountAddressNew = observer(({ addressStore, routeService, intl }) => {
  const [error, setError] = useState(null);
  const [requestId, setRequestId] = useState(0);
  const history = useHistory();

  const breadcrumbsPath = useMemo(() => {
    return routeService.transformBreadCrumbs([
      {
        text: intl.formatMessage(globalTranslations.myAccount),
        url: Paths.MyAccount,
      },
      {
        text: intl.formatMessage(globalTranslations.addressBookTitle),
        url: Paths.MyAccountAddressList,
      },
      {
        text: intl.formatMessage(globalTranslations.addNewAddressTitle),
        url: Paths.MyAccountAddressNew,
      },
    ]);
  }, [intl, routeService]);

  const navigateToAddressList = useCallback(() => {
    history.push(routeService.getPath(Paths.MyAccountAddressList));
  }, [history, routeService]);

  const saveAddress = useCallback((address) => {
    addressStore
      .saveAddress(address)
      .then(() => {
        navigateToAddressList();
      })
      .catch((error) => {
        setError(error);
        setRequestId(prev => prev + 1);
      });
  }, [addressStore, navigateToAddressList]);

  const renderError = () => {
    const status = get(error, 'response.status');
    return status === 422 ? (
      <Alert
        className="MyAccountAddressNew__errors"
        color="danger"
        key={requestId}
      >
        {flatten(Object.values(error.response.data)).map((message) => (
          <div key={message}>{message}</div>
        ))}
      </Alert>
    ) : (
      <UnexpectedError />
    );
  };

  return (
    <div className="MyAccountAddressNew">
      <SEOTitle title={globalTranslations.addNewAddressTitle} />
      <HelperNavigation breadcrumbsPath={breadcrumbsPath} />
      <PageTitle>
        <FormattedMessage {...globalTranslations.addNewAddressTitle} />
      </PageTitle>
      {error && renderError()}
      <AddressForm
        onSubmit={saveAddress}
        loading={addressStore.state === RequestState.LOADING}
        buttonText={
          <FormattedMessage {...globalTranslations.addNewAddressTitle} />
        }
      />
    </div>
  );
});

MyAccountAddressNew.propTypes = {
  addressStore: modelOf(AddressStore).isRequired,
  routeService: PropTypes.instanceOf(RouteService).isRequired,
  intl: intlShape.isRequired,
};

export default injectIntl(
  inject('addressStore', 'routeService')(MyAccountAddressNew)
);
